import { MFA_METHODS } from '../constants/constants'

const VALID_MFA_METHODS = Object.values(MFA_METHODS)

/**
 * Parses the mfaMethod property from the backend and applies fallback logic.
 *
 * @param mfaMethodRaw - The raw mfaMethod value from customer properties (typically a JSON string)
 * @param isMFARequired - Whether MFA is required for this customer
 * @returns An array of valid MFA method strings, null if MFA is not required and no method is configured
 *
 * Logic:
 * - If isMFARequired is false and mfaMethod is absent or malformed, return null
 * - If isMFARequired is true and mfaMethod is absent or malformed, return [MFA_METHODS.EMAIL] by default
 * - Else, return the parsed and validated array of MFA methods
 */
export const parseMFAMethods = (mfaMethodRaw: string | undefined, isMFARequired: boolean): string[] | null => {
  // Handle absent mfaMethod
  if (!mfaMethodRaw) {
    return isMFARequired ? [MFA_METHODS.EMAIL] : null
  }

  // Attempt to parse JSON and validate
  try {
    const parsed = JSON.parse(mfaMethodRaw)

    // Ensure it's a non-empty array
    if (!Array.isArray(parsed) || parsed.length === 0) {
      return isMFARequired ? [MFA_METHODS.EMAIL] : null
    }

    // Filter to only known MFA methods (preserves valid methods even if some are unrecognized)
    const validatedMethods = parsed.filter((method) => VALID_MFA_METHODS.includes(method))

    // If at least one valid method exists, return the filtered list
    if (validatedMethods.length > 0) {
      return validatedMethods
    }
    // If no valid methods found after filtering, fall through to default behavior
  } catch {
    // JSON parsing failed, fall through to default behavior
  }

  // No valid methods found or malformed data
  return isMFARequired ? [MFA_METHODS.EMAIL] : null
}
